import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const STATUS_OPTIONS = ['pending', 'reviewed', 'replied', 'closed'];
const STATUS_COLORS  = { pending: 'badge-orange', reviewed: 'badge-gray', replied: 'badge-green', closed: 'badge-gray' };

export default function AdminInquiries() {
  const [inquiries, setInquiries] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [filter,    setFilter]    = useState('');
  const [page,      setPage]      = useState(1);
  const [pagination,setPagination]= useState({});
  const [expanded,  setExpanded]  = useState(null);

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams({ page, limit: 15 });
    if (filter) params.set('status', filter);
    api.get(`/inquiries/admin?${params}`)
      .then(r => { setInquiries(r.data.data || []); setPagination(r.data.pagination || {}); })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [page, filter]); // eslint-disable-line

  const updateStatus = async (id, status) => {
    try {
      const r = await api.put(`/inquiries/admin/${id}/status`, { status });
      setInquiries(is => is.map(i => i._id === id ? r.data.data : i));
      toast.success('Status updated.');
    } catch { toast.error('Failed to update status.'); }
  };

  const deleteInquiry = async (id) => {
    if (!window.confirm('Delete this inquiry?')) return;
    try {
      await api.delete(`/inquiries/admin/${id}`);
      setInquiries(is => is.filter(i => i._id !== id));
      toast.success('Inquiry deleted.');
    } catch { toast.error('Failed to delete.'); }
  };

  const downloadPDF = async (id, number) => {
    try {
      const r = await api.get(`/inquiries/admin/${id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a   = document.createElement('a');
      a.href = url; a.download = `${number}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Download failed.'); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <h1 className="font-headline font-black text-3xl uppercase tracking-tight">Inquiries</h1>
          <p className="font-body text-sm text-secondary mt-1">{pagination.total || 0} total inquiries</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => { setFilter(''); setPage(1); }}
            className={`px-4 py-2 font-label text-xs font-bold uppercase tracking-wider border transition-colors ${!filter ? 'bg-on-surface text-white border-on-surface' : 'border-outline-variant text-secondary hover:border-on-surface hover:text-on-surface'}`}>
            All
          </button>
          {STATUS_OPTIONS.map(s => (
            <button key={s} onClick={() => { setFilter(s); setPage(1); }}
              className={`px-4 py-2 font-label text-xs font-bold uppercase tracking-wider border transition-colors ${filter === s ? 'bg-on-surface text-white border-on-surface' : 'border-outline-variant text-secondary hover:border-on-surface hover:text-on-surface'}`}>
              {s}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="space-y-3">{[...Array(5)].map((_, i) => <div key={i} className="h-16 skeleton" />)}</div>
      ) : inquiries.length === 0 ? (
        <div className="text-center py-20 text-secondary">
          <span className="material-symbols-outlined text-[60px] text-outline-variant">receipt_long</span>
          <p className="font-headline font-bold text-xl uppercase mt-3">No inquiries found</p>
        </div>
      ) : (
        <div className="space-y-3">
          {inquiries.map(inq => (
            <div key={inq._id} className="bg-white border border-outline-variant/20">
              {/* Row */}
              <div className="flex items-center gap-4 px-5 py-4 flex-wrap">
                <div className="flex-1 grid grid-cols-2 md:grid-cols-5 gap-3 min-w-0">
                  <div>
                    <p className="font-label text-[10px] uppercase tracking-widest text-secondary">Inquiry No.</p>
                    <p className="font-headline font-black text-sm text-primary-container">{inq.inquiryNumber}</p>
                  </div>
                  <div>
                    <p className="font-label text-[10px] uppercase tracking-widest text-secondary">Customer</p>
                    <p className="font-label text-sm font-bold text-on-surface truncate">{inq.userName}</p>
                    <p className="font-label text-[10px] text-secondary truncate">{inq.userEmail}</p>
                  </div>
                  <div>
                    <p className="font-label text-[10px] uppercase tracking-widest text-secondary">Mobile</p>
                    <p className="font-label text-sm text-on-surface">{inq.userMobile || '—'}</p>
                  </div>
                  <div>
                    <p className="font-label text-[10px] uppercase tracking-widest text-secondary">Items</p>
                    <p className="font-label text-sm font-bold text-on-surface">{inq.items?.length || 0}</p>
                  </div>
                  <div>
                    <p className="font-label text-[10px] uppercase tracking-widest text-secondary">Date</p>
                    <p className="font-label text-sm text-on-surface">
                      {new Date(inq.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </p>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
                  {/* Status selector */}
                  <select
                    value={inq.status}
                    onChange={e => updateStatus(inq._id, e.target.value)}
                    className={`border font-label text-xs font-bold uppercase px-2 py-1.5 cursor-pointer focus:outline-none transition-colors ${
                      inq.status === 'pending'  ? 'border-orange-300 text-orange-600 bg-orange-50' :
                      inq.status === 'replied'  ? 'border-green-300  text-green-600  bg-green-50'  :
                      'border-gray-300 text-gray-600 bg-gray-50'
                    }`}>
                    {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>

                  <button onClick={() => downloadPDF(inq._id, inq.inquiryNumber)}
                    className="p-1.5 text-secondary hover:text-primary-container transition-colors" title="Download PDF">
                    <span className="material-symbols-outlined text-[20px]">download</span>
                  </button>

                  <button onClick={() => setExpanded(expanded === inq._id ? null : inq._id)}
                    className="p-1.5 text-secondary hover:text-on-surface transition-colors" title="View details">
                    <span className={`material-symbols-outlined text-[20px] transition-transform duration-200 ${expanded === inq._id ? 'rotate-180' : ''}`}>
                      expand_more
                    </span>
                  </button>

                  <button onClick={() => deleteInquiry(inq._id)}
                    className="p-1.5 text-secondary hover:text-error transition-colors" title="Delete">
                    <span className="material-symbols-outlined text-[20px]">delete</span>
                  </button>
                </div>
              </div>

              {/* Expanded details */}
              {expanded === inq._id && (
                <div className="border-t border-outline-variant/20 px-5 pb-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                    {/* Customer details */}
                    <div>
                      <p className="font-label text-xs uppercase tracking-widest text-secondary mb-3">Customer Details</p>
                      <div className="space-y-1 text-sm">
                        <p><span className="font-bold text-secondary w-20 inline-block">Name:</span> {inq.userName}</p>
                        <p><span className="font-bold text-secondary w-20 inline-block">Email:</span> {inq.userEmail}</p>
                        <p><span className="font-bold text-secondary w-20 inline-block">Mobile:</span> {inq.userMobile || '—'}</p>
                        <p><span className="font-bold text-secondary w-20 inline-block">Address:</span> {inq.userAddress || '—'}</p>
                      </div>
                      {inq.notes && (
                        <div className="mt-4 p-3 bg-surface-container-low border-l-4 border-primary-container">
                          <p className="font-label text-xs uppercase tracking-widest text-secondary mb-1">Notes</p>
                          <p className="font-body text-sm">{inq.notes}</p>
                        </div>
                      )}
                    </div>

                    {/* Items */}
                    <div>
                      <p className="font-label text-xs uppercase tracking-widest text-secondary mb-3">Inquiry Items</p>
                      <table className="w-full text-sm border-collapse">
                        <thead>
                          <tr className="bg-surface-container">
                            <th className="text-left px-3 py-2 font-label text-[10px] uppercase tracking-widest text-secondary">Sr.</th>
                            <th className="text-left px-3 py-2 font-label text-[10px] uppercase tracking-widest text-secondary">Product</th>
                            <th className="text-left px-3 py-2 font-label text-[10px] uppercase tracking-widest text-secondary">Size</th>
                            <th className="text-right px-3 py-2 font-label text-[10px] uppercase tracking-widest text-secondary">Qty</th>
                          </tr>
                        </thead>
                        <tbody>
                          {inq.items.map((item, i) => (
                            <tr key={i} className="border-b border-surface-container">
                              <td className="px-3 py-2 text-secondary">{i + 1}</td>
                              <td className="px-3 py-2 font-bold">{item.productName}</td>
                              <td className="px-3 py-2 text-secondary">{item.sizeLabel || '—'}</td>
                              <td className="px-3 py-2 text-right font-bold">{item.quantity}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex justify-center gap-2 mt-6">
          <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}
            className="px-3 py-2 border border-outline-variant text-sm disabled:opacity-40 hover:bg-surface-container">
            <span className="material-symbols-outlined text-[18px]">chevron_left</span>
          </button>
          {[...Array(pagination.pages)].map((_, i) => (
            <button key={i} onClick={() => setPage(i + 1)}
              className={`w-9 h-9 text-sm font-bold ${page === i + 1 ? 'bg-primary-container text-white' : 'border border-outline-variant hover:bg-surface-container'}`}>
              {i + 1}
            </button>
          ))}
          <button disabled={page >= pagination.pages} onClick={() => setPage(p => p + 1)}
            className="px-3 py-2 border border-outline-variant text-sm disabled:opacity-40 hover:bg-surface-container">
            <span className="material-symbols-outlined text-[18px]">chevron_right</span>
          </button>
        </div>
      )}
    </div>
  );
}
